import request, { AxiosResponse } from "axios";
import { apiBase } from "../../constants";

// Shapes returned by the MCP key/connection endpoints (Item 3:
// McpKeysController + McpConnectionsController, mounted at /v1/mcp/*).
// These mirror domain/repos/mcp_api_key_repo.ts IMcpApiKey (minus the secret),
// and the AI apps that can still sign in, one per app and team member.

export interface MCPAPIKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  /** what it can use through MCP now (its member's switches and permissions today) */
  effectiveScopes?: string[];
  teamMemberId?: number | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string;
}

// Returned ONCE by POST /v1/mcp/keys. `key` is the plaintext that is never
// retrievable again. Display it to the user, then discard.
export interface MCPAPIKeyWithSecret extends MCPAPIKey {
  key: string;
}

export interface MCPConnection {
  clientId: string;
  clientName?: string | null;
  /** who allowed the app; null for an old grant that names no one */
  teamMemberId?: number | null;
  scopes: string[];
  /** what it can use through MCP now (its member's switches and permissions today) */
  effectiveScopes?: string[];
  /** the first sign-in that still works */
  connectedAt?: string | null;
  /** the latest sign-in or renewal */
  lastSignInAt?: string | null;
}

const mcpBase = `${apiBase}/v1/mcp`;

export const listMCPKeys = async (): Promise<MCPAPIKey[]> => {
  try {
    const res: AxiosResponse<{ keys: MCPAPIKey[] }> = await request({
      url: `${mcpBase}/keys`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    // Tolerate either { keys: [...] } or a bare array.
    return Array.isArray(res.data) ? res.data : res.data?.keys || [];
  } catch (e) {
    const response = e?.response;
    const msg =
      response?.data?.error?.message ||
      e.message ||
      "We couldn't load your MCP keys. ";
    // The status travels with the message: a 403 means "not yours to see", which
    // reloading the page never fixes.
    throw Object.assign(new Error(msg), { status: response?.status });
  }
};

export const createMCPKey = async (
  name: string,
  scopes: string[]
): Promise<MCPAPIKeyWithSecret> => {
  try {
    // Backend nests the secret: { key: { ...metadata, key: "enc_mcp_…" } }.
    // Unwrap to the inner object (whose `.key` is the one-time plaintext), the
    // same way listMCPKeys unwraps `{ keys: [...] }`.
    const res: AxiosResponse<{ key: MCPAPIKeyWithSecret }> = await request({
      url: `${mcpBase}/keys`,
      method: "POST",
      data: { name, scopes },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data.key;
  } catch (e) {
    // Only the server's own reason travels on. Without one the message is empty
    // and the form says it in its own words, not axios' "Request failed with
    // status code 500" or "Network Error".
    throw new Error(e?.response?.data?.error?.message || "");
  }
};

export const revokeMCPKey = async (id: string): Promise<void> => {
  try {
    await request({
      url: `${mcpBase}/keys/${encodeURIComponent(id)}`,
      method: "DELETE",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't revoke the MCP key. ";
    throw new Error(msg);
  }
};

export const listMCPConnections = async (): Promise<MCPConnection[]> => {
  try {
    const res: AxiosResponse<{
      connections: MCPConnection[];
    }> = await request({
      url: `${mcpBase}/connections`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return Array.isArray(res.data) ? res.data : res.data?.connections || [];
  } catch (e) {
    const response = e?.response;
    const msg =
      response?.data?.error?.message ||
      e.message ||
      "We couldn't load your connected AI apps. ";
    // As for the keys: a 403 means no MCP & AI access, which a reload never fixes.
    throw Object.assign(new Error(msg), { status: response?.status });
  }
};

/**
 * Disconnect one person's sign-in of an app. `teamMemberId` names whose (the
 * owner may name anyone; null for an app allowed by no one); left out, the
 * server takes the caller's own.
 */
export const revokeConnection = async (
  clientId: string,
  teamMemberId?: number | null
): Promise<void> => {
  const member =
    teamMemberId === undefined
      ? ""
      : `?teamMemberId=${teamMemberId === null ? "none" : teamMemberId}`;
  try {
    await request({
      url: `${mcpBase}/connections/${encodeURIComponent(clientId)}${member}`,
      method: "DELETE",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    const msg =
      e?.response?.data?.error?.message ||
      e.message ||
      "We couldn't disconnect this app. ";
    throw new Error(msg);
  }
};
